/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { TrialBalanceInfo } from "./info";
import { TrialBalanceService } from "services/trial-balance";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const emptyRow = () => ({
  accountCode: "",
  accountName: "",
  openingDebit: 0,
  openingCredit: 0,
  debitAmount: 0,
  creditAmount: 0,
  closingDebit: 0,
  closingCredit: 0,
});

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  rows: [emptyRow()],
  totalOpeningDebit: 0,
  totalOpeningCredit: 0,
  totalDebitAmount: 0,
  totalCreditAmount: 0,
  totalClosingDebit: 0,
  totalClosingCredit: 0,
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function TrialBalanceDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await TrialBalanceService.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            month: apiData.month || initialValues.month,
            year: apiData.year || initialValues.year,
            rows: apiData.rows?.length > 0 ? apiData.rows : initialValues.rows,
            totalOpeningDebit: apiData.totalOpeningDebit || 0,
            totalOpeningCredit: apiData.totalOpeningCredit || 0,
            totalDebitAmount: apiData.totalDebitAmount || 0,
            totalCreditAmount: apiData.totalCreditAmount || 0,
            totalClosingDebit: apiData.totalClosingDebit || 0,
            totalClosingCredit: apiData.totalClosingCredit || 0,
            reportDate: apiData.reportDate || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/trial-balance");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        month: fValues.month,
        year: fValues.year,
        rows: (fValues.rows || []).map(row => ({
          accountCode: row.accountCode,
          accountName: row.accountName,
          openingDebit: num(row.openingDebit),
          openingCredit: num(row.openingCredit),
          debitAmount: num(row.debitAmount),
          creditAmount: num(row.creditAmount),
          closingDebit: num(row.closingDebit),
          closingCredit: num(row.closingCredit),
        })),
        totalOpeningDebit: num(fValues.totalOpeningDebit),
        totalOpeningCredit: num(fValues.totalOpeningCredit),
        totalDebitAmount: num(fValues.totalDebitAmount),
        totalCreditAmount: num(fValues.totalCreditAmount),
        totalClosingDebit: num(fValues.totalClosingDebit),
        totalClosingCredit: num(fValues.totalClosingCredit),
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await TrialBalanceService.create(payload);
        toast.success("Tạo bảng cân đối thành công!");
      } else {
        await TrialBalanceService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/trial-balance");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S06-DN</title>
      <style>@media print{@page{size:A4 portrait;margin:1.5cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <TrialBalanceInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Bảng S06-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default TrialBalanceDetail;
